import React, { useEffect, useMemo, useRef } from "react";

import { Icon } from "@braze/beacon-core";

import { useAgenticAiContext } from "@services/useAgenticAiContext";
import useI18nNamespace from "@services/useI18nNamespace";

import { chartDataParameters, chartDataToolDescription } from "../constants";
import type {
  OperatorChartData,
  OperatorChartDataAccess,
  OperatorChartDataSliceOptions,
  OperatorChartDataSlice,
} from "../types";
import { getOperatorChartDataAccess } from "./getOperatorChartDataAccess";
import { getOperatorTimeSeriesDataSlice } from "./getOperatorTimeSeriesDataSlice";

/**
 * Shares Operator chart metadata and the bounded chart-data tool between
 * Highcharts and charts whose rendering library is Beacon. Callers choose the
 * data explicitly: null clears the surface, while undefined is reserved for
 * the Highcharts wrapper's legacy extraction path.
 */
export const useOperatorChartData = (
  normalizedData: OperatorChartData | null | undefined,
  chartId: string
): OperatorChartDataAccess | undefined => {
  const { registerTool } = useAgenticAiContext();
  const { t } = useI18nNamespace("highcharts-wrapper");
  const operatorDataAccess = useMemo(
    () => (normalizedData ? getOperatorChartDataAccess(normalizedData, chartId) : undefined),
    [chartId, normalizedData]
  );
  const latestOperatorDataRef = useRef<OperatorChartData | undefined>(operatorDataAccess?.data);
  latestOperatorDataRef.current = operatorDataAccess?.data;
  const operatorDataRef = useRef<OperatorChartData | undefined>(operatorDataAccess?.data);
  operatorDataRef.current = operatorDataAccess?.data;
  const requiresChartDataTool = operatorDataAccess?.requiresTool === true;

  useEffect(() => {
    if (!requiresChartDataTool) {
      return;
    }

    // StrictMode and dependency changes run cleanup before the replacement effect. Restore the
    // current data before registering the new callback after cleanup has cleared the live ref.
    operatorDataRef.current = latestOperatorDataRef.current;
    const cleanup = registerTool({
      toolEntityId: chartId,
      name: "get_chart_data",
      description: chartDataToolDescription,
      parameters: chartDataParameters,
      callback: (args: OperatorChartDataSliceOptions): OperatorChartDataSlice => {
        const data = operatorDataRef.current;
        if (!data) {
          throw new Error("Chart data is no longer available.");
        }

        return getOperatorTimeSeriesDataSlice(data, args);
      },
      options: {
        toolGroup: "Analysis",
        labels: {
          ready: t("read-chart-data-ready", "Preparing to read chart data"),
          running: t("read-chart-data-running", "Reading chart data"),
          complete: t("read-chart-data-complete", "Read chart data"),
          failed: t("read-chart-data-failed", "Failed to read chart data"),
          stopped: t("read-chart-data-stopped", "Stopped reading chart data"),
        },
        requiresApproval: "no",
        allowJsonPathTraversal: true,
        toolIcon: <Icon name={["far", "chart-line"]} />,
        argumentsSummaryText: () =>
          operatorDataRef.current?.series.map(({ name }) => name).join(", ") || t("chart-data", "Chart data"),
      },
    });

    return () => {
      operatorDataRef.current = undefined;
      cleanup();
    };
  }, [chartId, registerTool, requiresChartDataTool, t]);

  return operatorDataAccess;
};
